// src/Pages/viewBlog/ViewBlog.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './viewblog.scss';

import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';

import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const ViewBlog = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        const fetchArticle = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await axios.get(`https://api.moroccool.com/api/blogs/${id}`);
                setBlog(res.data);
            } catch (err) {
                console.error('Error loading article:', err);
                setError('Impossible de charger l’article.');
            } finally {
                setLoading(false);
            }
        };
        fetchArticle();
    }, [id]);

    const formatDate = (date) =>
        date
            ? new Date(date).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            })
            : '';

    const renderBlock = (block, idx) => {
        switch (block.type) {
            case 'heading': {
                const Tag = `h${block.level || 2}`;
                return <Tag key={idx} className="view-blog-block-heading">{block.text}</Tag>;
            }
            case 'cta':
                return (
                    <a
                        key={idx}
                        href={block.url || '#'}
                        className="view-blog-block-cta"
                    >
                        {block.text || 'En savoir plus'}
                    </a >
                );
            case 'paragraph':
            default:
                return (
                    <p key={idx} className="view-blog-block-paragraph">
                        {block.text}
                    </p>
                );
        }
    };

    return (
        <div className="view-blog-admin">
            <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            <div className="view-blog-main">
                <Navbar />

                <div className="view-blog-toolbar">
                    <button className="view-blog-back" onClick={() => navigate('/blogs')}>
                        <ArrowBackIcon /> Retour à la liste
                    </button>
                    {blog && (
                        <button
                            className="view-blog-edit-btn"
                            onClick={() => navigate(`/blogs/${id}`)}
                        >
                            <EditIcon /> Modifier
                        </button>
                    )}
                </div>

                {loading && (
                    <div className="view-blog-state-loading">
                        <div className="view-blog-spinner" />
                        <p>Chargement…</p>
                    </div>
                )}

                {error && !loading && <div className="view-blog-error">{error}</div>}

                {!loading && !error && blog && (
                    <article className="view-blog-article">
                        {blog.heroImage && (
                            <div className="view-blog-hero">
                                <img src={blog.heroImage} alt={blog.title} />
                            </div>
                        )}

                        <div className="view-blog-body">
                            <div className="view-blog-meta">
                                {blog.category && (
                                    <span className="view-blog-category">{blog.category}</span>
                                )}
                                <span
                                    className={`view-blog-status ${blog.status === 'published' ? 'is-published' : 'is-draft'
                                        }`}
                                >
                                    {blog.status === 'published' ? 'Publié' : 'Brouillon'}
                                </span>
                                {blog.featured && <span className="view-blog-featured">⭐ Vedette</span>}
                            </div>

                            <h1 className="view-blog-title">{blog.title}</h1>
                            {blog.subtitle && <p className="view-blog-subtitle">{blog.subtitle}</p>}

                            <div className="view-blog-byline">
                                <span><PersonOutlineIcon fontSize="small" /> {blog.author}</span>
                                <span><CalendarTodayIcon fontSize="small" /> {formatDate(blog.createdAt)}</span>
                            </div>

                            {blog.excerpt && <p className="view-blog-excerpt">{blog.excerpt}</p>}

                            <div className="view-blog-content">
                                {blog.content?.map(renderBlock)}
                            </div>

                            {blog.gallery?.length > 0 && (
                                <div className="view-blog-gallery">
                                    {blog.gallery.map((img, idx) => (
                                        <figure key={idx} className="view-blog-gallery-item">
                                            <img src={img.src} alt={img.alt || ''} />
                                            {img.caption && <figcaption>{img.caption}</figcaption>}
                                        </figure>
                                    ))}
                                </div>
                            )}

                            {blog.tags?.length > 0 && (
                                <div className="view-blog-tags">
                                    {blog.tags.map((tag) => (
                                        <span key={tag} className="view-blog-tag">#{tag}</span>
                                    ))}
                                </div>
                            )}

                            {blog.relatedArticles?.length > 0 && (
                                <div className="view-blog-related">
                                    <h3>Articles suggérés</h3>
                                    <ul>
                                        {blog.relatedArticles.map((a) => (
                                            <li key={a._id}>
                                                <span
                                                    className="view-blog-related-link"
                                                    onClick={() => navigate(`/blogs/view/${a._id}`)}
                                                >
                                                    {a.title}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </article>
                )}

                <footer className="view-blog-footer">
                    © {new Date().getFullYear()} <span>Moroccool</span> — Tous droits réservés.
                </footer>
            </div>
        </div>
    );
};

export default ViewBlog;